/**
 * Client-side preparation of a picked avatar image, before `POST /profile/avatar`.
 *
 * THREE jobs, and the third is the one that was missing:
 * 1. FORMAT — the server decodes JPEG/PNG/GIF/BMP only (jimp has no webp/HEIC
 *    codec), but a phone's `accept="image/*"` picker happily produces webp
 *    (Android) or HEIC (iOS "original"). Those are transcoded to JPEG here using
 *    the browser's own decoder (`createImageBitmap` bakes EXIF orientation in),
 *    so the pick "just works" instead of 400ing.
 * 2. SIZE — capped at {@link MAX_AVATAR_BYTES} (nginx's 2 MB, the limit that
 *    actually bites). Rather than bouncing a perfectly good 8 MB camera JPEG
 *    back at the user, an oversize pick is downscaled through the transcode
 *    path (a ≤1024 px JPEG is far below the cap).
 * 3. PROOF OF DECODABILITY — every pick is decoded HERE, so the crop sheet is
 *    never handed a file this device cannot render. That used to be possible
 *    (jpeg/png went through untouched) and it is what made the sheet sit black
 *    forever for 60 users. A pick that will not decode surfaces as a typed
 *    {@link AvatarFileError} with a message, never as an empty sheet.
 */

/**
 * Max upload body — **nginx's `client_max_body_size` (2 MB), which is the limit that actually
 * bites.** Three places used to disagree: nginx `2m` on all five vhosts, the api route's
 * `bodyLimit` of 5 MB, and this constant at 5 MB with a comment claiming it mirrored the api. nginx
 * rejects first, so a 3 MB pick was accepted here and then 413'd on the wire — the user being told
 * «too large» for a file the client had just approved. The api's 5 MB is a harmless backstop BEHIND
 * this; if it is ever raised here, raise nginx first.
 */
export const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

/** Content-types the api can parse AND jimp can decode — safe to send verbatim. */
const DIRECT_UPLOAD_TYPES: ReadonlySet<string> = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/bmp",
]);

/** Longest side of a client transcode — comfortable headroom over the server's 512 output. */
const TRANSCODE_MAX_DIM = 1024;

export type AvatarFileIssue = "too_large" | "unsupported";

/** A pick the client provably cannot turn into an uploadable image. */
export class AvatarFileError extends Error {
  constructor(readonly issue: AvatarFileIssue) {
    super(issue === "too_large" ? "Image exceeds the 2 MB limit" : "Unsupported image format");
    this.name = "AvatarFileError";
  }
}

export interface PreparedAvatar {
  blob: Blob;
  mime: string;
}

/** Decode with EXIF orientation applied. "from-image" is the modern default,
 * but it is passed EXPLICITLY so the re-encode is provably upright; engines
 * that reject the options bag get a bare retry (their decode behaves the same). */
async function decodeOrientedBitmap(file: Blob): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== "function") return null;
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Fall through: some engines throw on the options bag itself.
  }
  try {
    return await createImageBitmap(file);
  } catch {
    return null;
  }
}

/**
 * Why this is a three-way result and not `Blob | null`: "no JPEG came back" has two opposite
 * meanings. If the platform has no decode/canvas pipeline AT ALL we must fall back to sending the
 * original (the old behaviour, still right for an ancient WebView). But if the pipeline IS there
 * and refused the bytes, the file is not renderable HERE — and handing it to the crop sheet's
 * `<img>` produces the silent black box that left 60 users unable to set a photo. Those two cases
 * need opposite answers, so they cannot share a `null`.
 */
type TranscodeResult =
  { kind: "ok"; blob: Blob } | { kind: "undecodable" } | { kind: "unavailable" };

/** Decode with the browser and re-encode as a ≤1024px JPEG. */
async function transcodeToJpeg(file: Blob): Promise<TranscodeResult> {
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") {
    return { kind: "unavailable" };
  }
  const bitmap = await decodeOrientedBitmap(file);
  if (!bitmap) return { kind: "undecodable" }; // decoder present, bytes refused
  try {
    const scale = Math.min(1, TRANSCODE_MAX_DIM / Math.max(bitmap.width, bitmap.height, 1));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return { kind: "unavailable" };
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.9);
    });
    return blob ? { kind: "ok", blob } : { kind: "unavailable" };
  } catch {
    return { kind: "unavailable" };
  } finally {
    bitmap.close();
  }
}

/** Side of the square the client-side crop renders — matches the server's 512 output. */
export const AVATAR_CROP_SIZE = 512;

/** A square region of the SOURCE image, in source-pixel coordinates. */
export interface AvatarCropRect {
  x: number;
  y: number;
  size: number;
}

/**
 * Render the chosen square of the source image to the final avatar — a
 * {@link AVATAR_CROP_SIZE}² JPEG (q0.8). The source is the cropper's own loaded
 * `<img>`, so what is drawn is exactly what was framed (same EXIF-oriented
 * space). Returns null when the platform lacks canvas support — the caller then
 * uploads the uncropped image and the server's center-crop still applies.
 */
export async function renderAvatarCrop(
  source: CanvasImageSource,
  rect: AvatarCropRect,
): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_CROP_SIZE;
    canvas.height = AVATAR_CROP_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    // JPEG has no alpha: flatten transparent PNG picks onto white, not black.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, AVATAR_CROP_SIZE, AVATAR_CROP_SIZE);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      source,
      rect.x,
      rect.y,
      rect.size,
      rect.size,
      0,
      0,
      AVATAR_CROP_SIZE,
      AVATAR_CROP_SIZE,
    );
    return await new Promise<Blob | null>((resolve) => {
      // q0.8, not q0.9: the upload BODY is what gets severed mid-TLS on these networks (10.6% of
      // avatar uploads and 4.9% of voice notes failed that way on prod), and every 16 KiB TLS
      // record is another chance to be cut. The server re-encodes to q82 anyway, and the largest
      // an avatar is ever drawn is 96px.
      canvas.toBlob(resolve, "image/jpeg", 0.8);
    });
  } catch {
    return null;
  }
}

/**
 * Turn a picked file into an upload the api will accept, or throw {@link AvatarFileError} with the
 * precise reason.
 *
 * EVERY decodable pick is re-encoded here — there is deliberately no "server-safe types pass
 * through untouched" fast path any more. That shortcut was the bug: it handed the ORIGINAL
 * full-resolution file to the crop sheet's `<img>`, and a low-memory Android WebView cannot decode
 * a 12–100 MP photo, so the sheet sat black forever (60 users in 5 observed days, 40 of them still
 * on a preset avatar). Decoding HERE does two jobs at once — it PROVES the pick is renderable
 * before the cropper depends on it, and it caps the image at 1024px, which also shrinks the upload
 * body that these networks sever mid-TLS.
 */
export async function prepareAvatarUpload(file: File): Promise<PreparedAvatar> {
  const transcoded = await transcodeToJpeg(file);
  if (transcoded.kind === "ok") {
    if (transcoded.blob.size > MAX_AVATAR_BYTES) throw new AvatarFileError("too_large");
    return { blob: transcoded.blob, mime: "image/jpeg" };
  }

  // The platform HAS a decoder and it refused these bytes (a mislabelled HEIC, or an image beyond
  // the WebView's decoder). The crop sheet would render exactly the same nothing, so say so now
  // with a message the user can act on rather than opening a sheet that cannot work.
  if (transcoded.kind === "undecodable") throw new AvatarFileError("unsupported");

  // No decode pipeline at all (ancient WebView): fall back to the original and let the server's
  // own decode + center-crop be the safety net, exactly as before.
  if (DIRECT_UPLOAD_TYPES.has(file.type)) {
    if (file.size > MAX_AVATAR_BYTES) throw new AvatarFileError("too_large");
    return { blob: file, mime: file.type };
  }
  throw new AvatarFileError("unsupported");
}
